
























































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import { BCol, BRow } from 'bootstrap-vue';
import { ZenFormGroup } from 'zenComponents';
import ZenMultiselect from '@reciprocity/multiselect';
import { flow, get, keyBy, sortBy, uniqBy } from 'lodash';
import { WORKFLOW_TRANSITIONS_QUERY } from '@/pages/workflows/WorkflowsGqlHelper';

const WorkflowTriggerSelector = Vue.extend({
  name: 'WorkflowTriggerSelector',
  components: {
    ZenFormGroup,
    BCol,
    BRow,
    ZenMultiselect,
  },
  props: {
    step: {
      type: Object,
      required: true,
      validator: step => !!step.id,
    },
  },
  data() {
    return {
      actionOptions: [{ id: 0, label: 'Create task' }],
      initDropdowns: false,
    };
  },
  apollo: {
    gqlWorkflowTriggers: {
      query: WORKFLOW_TRANSITIONS_QUERY,
      update: result => result,
      error() {
        this.showAlert({
          type: 'error',
          message: 'Failed to load step.',
        });
      },
    },
  },
  computed: {
    ...mapGetters(['featureFlags']),
    transitionsByObjectType() {
      return keyBy(get(this.gqlWorkflowTriggers, 'workflow.value'), 'objectType');
    },
    selectedObjectType() {
      const objectType = this.step.sourceType;
      return { id: objectType, label: objectType };
    },
    selectedEvent() {
      return this.optionForEventType(this.step.triggerType);
    },
    selectedStateTransition() {
      const parameter = this.step.actionTrigger;
      return parameter ? { id: parameter, label: parameter } : undefined;
    },
    selectedRiskThreshold() {
      return this.step.riskThreshold;
    },
    objectTypeOptions() {
      return Object.keys(this.transitionsByObjectType || {}).map(objectType => ({ id: objectType, label: objectType }));
    },
    eventOptions() {
      return this.triggerTypesForObject(this.step.sourceType).map(this.optionForEventType);
    },
    stateTransitionOptions() {
      return this.stateTransitionOptionsForObject(this.step.sourceType);
    },
    riskThresholdOptions() {
      const transformTreshold = threshold => ({
        id: get(threshold, 'node.id'),
        label: get(threshold, 'node.title.value'),
      });
      const scores = get(this.gqlWorkflowTriggers, 'riskThresholds.edges', []);
      return scores.map(score => ({
        groupId: get(score, 'node.id'),
        groupName: get(score, 'node.title.value'),
        thresholds: get(score, 'node.thresholds.value.edges', []).map(transformTreshold),
      }));
    },
  },
  mounted() {
    // Multiselects initialization blocks rendering steps:
    this.$nextTick(() => {
      this.initDropdownsTimer = setTimeout(() => {
        this.initDropdowns = true;
      }, 300);
    });
  },
  beforeDestroy() {
    clearTimeout(this.initDropdownsTimer);
  },
  methods: {
    ...mapActions({ showAlert: 'alerts/show' }),
    ...mapActions('workflows', ['updateStep']),
    onObjectTypeChange(value: Object) {
      // unset dependent options unless there is only a single option which is the case for some trigger types
      const preselectedTriggerType = this.preselectedTriggerType(value.id);
      this.updateStep({
        id: this.step.id,
        data: {
          sourceType: value.id,
          triggerType: preselectedTriggerType,
          actionTrigger: this.preselectedStateTransition(value.id, preselectedTriggerType),
          riskThreshold: this.preselectedRiskThreshold(preselectedTriggerType),
        },
      });
    },
    onEventTypeChange(value: Object) {
      this.updateStep({
        id: this.step.id,
        data: {
          triggerType: value.id,
          actionTrigger: this.preselectedStateTransition(this.step.sourceType, value.id),
          riskThreshold: this.preselectedRiskThreshold(value.id),
        },
      });
    },
    onParameterChange(value: Object) {
      this.updateStep({
        id: this.step.id,
        data: {
          actionTrigger: value.id,
        },
      });
    },
    onRiskThresholdChange(value: Object) {
      this.updateStep({
        id: this.step.id,
        data: {
          riskThreshold: value,
        },
      });
    },
    triggerTypesForObject(objectType: string) {
      if (objectType == 'Risk') {
        return ['WORKFLOW_ACTION', 'RISK_THRESHOLD'];
      } else {
        return ['WORKFLOW_ACTION'];
      }
    },
    stateTransitionOptionsForObject(objectType: string) {
      const objectTransitions = get(this.transitionsByObjectType, `${objectType}.transitions`) || [];

      return flow(
        transitions => uniqBy(transitions, 'name'),
        transitions => sortBy(transitions, 'name'),
        transitions => transitions.map(it => ({ id: it.name, label: it.name }))
      )(objectTransitions);
    },
    optionForEventType(eventType: string) {
      if (!eventType) {
        return undefined;
      }

      const labelsByType: { [key: string]: string } = {
        WORKFLOW_ACTION: 'Status transition',
        RISK_THRESHOLD: 'Threshold reached',
      };
      return { id: eventType, label: labelsByType[eventType] };
    },
    preselectedTriggerType(objectType: string) {
      const possibleTriggers = this.triggerTypesForObject(objectType);
      return possibleTriggers.length === 1 ? possibleTriggers[0] : null;
    },
    preselectedStateTransition(objectType: string, triggerType: string) {
      if (triggerType == 'WORKFLOW_ACTION') {
        const stateTransitionOptions = this.stateTransitionOptionsForObject(objectType);
        return stateTransitionOptions.length === 1 ? stateTransitionOptions[0].id : null;
      } else {
        return null;
      }
    },
    preselectedRiskThreshold(triggerType: string) {
      if (triggerType !== 'RISK_THRESHOLD') {
        return null;
      }
      if (this.riskThresholdOptions.length !== 1) {
        return null;
      }
      const thresholdOptions = this.riskThresholdOptions[0].thresholds;
      return thresholdOptions.length === 1 ? thresholdOptions[0] : null;
    },
  },
});
export default WorkflowTriggerSelector;
